import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import type { MapRef } from 'react-map-gl/mapbox'
import { Marker } from 'react-map-gl/mapbox'
import Supercluster from 'supercluster'

const CLUSTER_RADIUS = 60
const CLUSTER_MAX_ZOOM = 16
const CLUSTER_MIN_POINTS = 2
const MAX_EXPANSION_ZOOM = 20
const CLUSTER_EXPAND_ANIMATION_MS = 500
const WORLD_BOUNDS: [number, number, number, number] = [-180, -85, 180, 85]

type MarkerBase = { lat: number; lng: number }

type MarkerPointProperties<T extends MarkerBase> = T & { originalIndex: number; cluster?: undefined }
type PointFeature<T extends MarkerBase> = Supercluster.PointFeature<MarkerPointProperties<T>>
type ClusterFeature = Supercluster.ClusterFeature<{}>

export function isClusterFeature<T extends MarkerBase>(
  feature: ClusterFeature | PointFeature<T>,
): feature is ClusterFeature {
  return feature.properties.cluster === true
}

type ClusterSizeClass = 'cluster-s' | 'cluster-m' | 'cluster-l'

function getClusterSizeClass(pointCount: number): ClusterSizeClass {
  if (pointCount >= 20) return 'cluster-l'
  if (pointCount >= 10) return 'cluster-m'
  return 'cluster-s'
}

const ClusterCircle = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.default.eight};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow.flat.large};
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.12);
  }

  &.cluster-s {
    width: ${rem(32)};
    height: ${rem(32)};
    font-size: ${rem(16)};
  }

  &.cluster-m {
    width: ${rem(38)};
    height: ${rem(38)};
    font-size: ${rem(16)};
  }

  &.cluster-l {
    width: ${rem(40)};
    height: ${rem(40)};
    font-size: ${rem(17)};
  }
`

interface Props {
  clusterId: number
  lng: number
  lat: number
  pointCount: number
  onClick: (clusterId: number, lng: number, lat: number) => void
}

export const ClusterMarker = React.memo(function ClusterMarker({
  clusterId,
  lng,
  lat,
  pointCount,
  onClick,
}: Props) {
  const handleClick = useCallback(() => {
    onClick(clusterId, lng, lat)
  }, [onClick, clusterId, lng, lat])

  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="center"
    >
      <ClusterCircle className={getClusterSizeClass(pointCount)} onClick={handleClick}>
        {pointCount}
      </ClusterCircle>
    </Marker>
  )
})

interface UseMapClustersArgs<T extends MarkerBase> {
  markers: Array<T>
  mapRef: React.RefObject<MapRef | null>
  zoom: number
  /** When true, skip Supercluster and return every marker as an individual point feature */
  disableClustering?: boolean
}

export function useMapClusters<T extends MarkerBase>({
  markers,
  mapRef,
  zoom,
  disableClustering = false,
}: UseMapClustersArgs<T>) {
  const geojsonPoints: Array<PointFeature<T>> = useMemo(() => markers.map((marker, index) => ({
    type: 'Feature' as const,
    properties: { ...marker, originalIndex: index },
    geometry: {
      type: 'Point' as const,
      coordinates: [marker.lng, marker.lat] as [number, number],
    },
  })), [markers])

  const clusterIndex = useMemo(() => {
    if (disableClustering) return null
    const index = new Supercluster<MarkerPointProperties<T>, {}>({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
      minPoints: CLUSTER_MIN_POINTS,
    })
    index.load(geojsonPoints)
    return index
  }, [geojsonPoints, disableClustering])

  const flooredZoom = Math.floor(zoom)

  const clusters = useMemo(
    () => {
      if (!clusterIndex) return geojsonPoints
      return clusterIndex.getClusters(WORLD_BOUNDS, flooredZoom)
    },
    [clusterIndex, flooredZoom, geojsonPoints],
  )

  const handleClusterClick = useCallback((clusterId: number, lng: number, lat: number) => {
    if (!clusterIndex) return
    const expansionZoom = Math.min(clusterIndex.getClusterExpansionZoom(clusterId), MAX_EXPANSION_ZOOM)
    mapRef.current?.getMap()?.flyTo({
      center: [lng, lat],
      zoom: expansionZoom,
      duration: CLUSTER_EXPAND_ANIMATION_MS,
      essential: true,
    })
  }, [clusterIndex, mapRef])

  return { clusters, handleClusterClick }
}
